import { UserRole } from '@/components/authnz/types/rbac';
import type { PrototypeAction, PrototypeRow } from './types';
import { SELF_EMAIL } from './types';

export function seedRows(): PrototypeRow[] {
  return [
    {
      emailKey: SELF_EMAIL,
      lifecycle: 'existing',
      grants: [{ role: UserRole.ADMINISTRATOR, source: 'admin' }],
      lastSignIn: '2026-09-12T10:00:00Z',
      knownUids: 1,
      invitedNotSignedIn: false,
      authExists: true,
      authDisabled: false,
      emailVerified: true,
      activeSubscriptions: [],
      lastSessionError: null,
      lastSignInLinkSend: null,
      fallbackLegacy: false,
    },
    {
      emailKey: 'jane@clinic.com',
      lifecycle: 'existing',
      grants: [
        { role: UserRole.PRACTITIONER, source: 'subscription' },
        { role: UserRole.TUTOR, source: 'admin' },
      ],
      lastSignIn: '2026-09-11T08:00:00Z',
      knownUids: 1,
      invitedNotSignedIn: false,
      authExists: true,
      authDisabled: false,
      emailVerified: true,
      activeSubscriptions: ['prod_practitioner_monthly'],
      lastSessionError: null,
      lastSignInLinkSend: null,
      fallbackLegacy: false,
    },
    {
      emailKey: 'invited@clinic.com',
      lifecycle: 'existing',
      grants: [{ role: UserRole.TUTOR, source: 'seed' }],
      lastSignIn: null,
      knownUids: 0,
      invitedNotSignedIn: true,
      authExists: false,
      authDisabled: false,
      emailVerified: false,
      activeSubscriptions: [],
      lastSessionError: null,
      lastSignInLinkSend: '2026-09-10T12:00:00Z',
      fallbackLegacy: false,
    },
    {
      emailKey: 'gone@clinic.com',
      lifecycle: 'deleted',
      grants: [],
      lastSignIn: '2026-08-01T00:00:00Z',
      knownUids: 1,
      invitedNotSignedIn: false,
      authExists: true,
      authDisabled: true,
      emailVerified: true,
      activeSubscriptions: [],
      lastSessionError: 'offboarded',
      lastSignInLinkSend: null,
      fallbackLegacy: false,
    },
    {
      emailKey: 'disabled@clinic.com',
      lifecycle: 'existing',
      grants: [{ role: UserRole.PRACTITIONER, source: 'admin' }],
      lastSignIn: '2026-09-01T00:00:00Z',
      knownUids: 1,
      invitedNotSignedIn: false,
      authExists: true,
      authDisabled: true,
      emailVerified: true,
      activeSubscriptions: [],
      lastSessionError: 'disabled',
      lastSignInLinkSend: null,
      fallbackLegacy: false,
    },
    {
      emailKey: 'stripe@clinic.com',
      lifecycle: 'existing',
      grants: [{ role: UserRole.STUDENT, source: 'subscription' }],
      lastSignIn: '2026-09-09T00:00:00Z',
      knownUids: 1,
      invitedNotSignedIn: false,
      authExists: true,
      authDisabled: false,
      emailVerified: true,
      activeSubscriptions: ['prod_student_monthly'],
      lastSessionError: null,
      lastSignInLinkSend: null,
      fallbackLegacy: false,
    },
    {
      emailKey: 'fallback@clinic.com',
      lifecycle: 'existing',
      grants: [{ role: UserRole.SALES, source: 'admin' }],
      lastSignIn: '2026-09-08T00:00:00Z',
      knownUids: 1,
      invitedNotSignedIn: false,
      authExists: true,
      authDisabled: false,
      emailVerified: true,
      activeSubscriptions: [],
      lastSessionError: null,
      lastSignInLinkSend: null,
      fallbackLegacy: true,
    },
  ];
}

export function applyAction(
  rows: PrototypeRow[],
  action: PrototypeAction,
): { rows: PrototypeRow[]; log: string; error?: string } {
  const clone = rows.map((row) => ({ ...row, grants: [...row.grants] }));
  const find = (emailKey: string) =>
    clone.find((row) => row.emailKey === emailKey);

  if (action.type === 'invite' || action.type === 'assign') {
    const existing = find(action.emailKey);
    if (existing?.lifecycle === 'deleted') {
      return {
        rows,
        log: `409 assign ${action.emailKey}`,
        error: 'deleted-409',
      };
    }
    if (!existing) {
      clone.push({
        emailKey: action.emailKey,
        lifecycle: 'existing',
        grants: [{ role: action.role, source: 'admin' }],
        lastSignIn: null,
        knownUids: 0,
        invitedNotSignedIn: true,
        authExists: false,
        authDisabled: false,
        emailVerified: false,
        activeSubscriptions: [],
        lastSessionError: null,
        lastSignInLinkSend: new Date().toISOString(),
        fallbackLegacy: false,
      });
      return { rows: clone, log: `invite ${action.emailKey} ${action.role}` };
    }
    if (!existing.grants.some((grant) => grant.role === action.role)) {
      existing.grants.push({ role: action.role, source: 'admin' });
    }
    return { rows: clone, log: `assign ${action.emailKey} ${action.role}` };
  }

  if (action.type === 'revoke-admin') {
    const row = find(action.emailKey);
    if (!row) return { rows, log: 'missing' };
    const chip = row.grants.find((grant) => grant.role === action.role);
    if (!chip || chip.source !== 'admin') {
      return { rows, log: `cannot revoke ${action.role}`, error: 'own-source' };
    }
    if (
      action.role === UserRole.ADMINISTRATOR &&
      clone.filter(
        (item) =>
          item.lifecycle === 'existing' &&
          item.grants.some((grant) => grant.role === UserRole.ADMINISTRATOR),
      ).length === 1
    ) {
      return { rows, log: 'last-admin refused', error: 'last-admin' };
    }
    if (action.emailKey === SELF_EMAIL && action.role === UserRole.ADMINISTRATOR) {
      return { rows, log: 'self-demotion refused', error: 'self' };
    }
    row.grants = row.grants.filter((grant) => grant.role !== action.role);
    return { rows: clone, log: `revoke ${action.emailKey} ${action.role}` };
  }

  if (action.type === 'delete') {
    const row = find(action.emailKey);
    if (!row) return { rows, log: 'missing' };
    if (action.emailKey === SELF_EMAIL) {
      return { rows, log: 'self delete refused', error: 'self' };
    }
    row.lifecycle = 'deleted';
    row.grants = [];
    row.authDisabled = true;
    row.activeSubscriptions = [];
    row.lastSessionError = 'offboarded';
    return { rows: clone, log: `delete ${action.emailKey}` };
  }

  if (action.type === 'restore') {
    const row = find(action.emailKey);
    if (!row) return { rows, log: 'missing' };
    row.lifecycle = 'existing';
    row.authDisabled = false;
    row.lastSessionError = null;
    return { rows: clone, log: `restore ${action.emailKey}` };
  }

  if (action.type === 'move-email') {
    const row = find(action.from);
    if (!row) return { rows, log: 'missing' };
    if (find(action.to)) {
      return { rows, log: 'target exists', error: 'collision' };
    }
    row.emailKey = action.to;
    return { rows: clone, log: `move ${action.from} → ${action.to}` };
  }

  if (action.type === 'copy-link' || action.type === 'send-link') {
    const row = find(action.emailKey);
    if (!row) return { rows, log: 'missing' };
    row.lastSignInLinkSend = new Date().toISOString();
    return { rows: clone, log: `${action.type} ${action.emailKey}` };
  }

  return { rows, log: 'noop' };
}

export function explainAccess(row: PrototypeRow): string {
  const lines = [
    `emailKey: ${row.emailKey}`,
    `lifecycle: ${row.lifecycle}`,
    `authExists: ${row.authExists}`,
    `authDisabled: ${row.authDisabled}`,
    `emailVerified: ${row.emailVerified}`,
    `invitedNotSignedIn: ${row.invitedNotSignedIn}`,
    `grants: ${row.grants.map((grant) => `${grant.role}:${grant.source}`).join(', ') || '(none)'}`,
    `subscriptions: ${row.activeSubscriptions.join(', ') || '(none)'}`,
    `lastSessionError: ${row.lastSessionError ?? '(none)'}`,
    `lastSignInLinkSend: ${row.lastSignInLinkSend ?? '(none)'}`,
    `knownUids: ${row.knownUids}`,
    `fallbackLegacy: ${row.fallbackLegacy}`,
  ];
  if (row.authDisabled && row.lifecycle === 'existing') {
    lines.push('NOTE: Auth disabled without deleted grant — diagnostic only, not auto-delete.');
  }
  return lines.join('\n');
}

export function problemLabel(row: PrototypeRow): string | null {
  if (row.lifecycle === 'deleted') return 'deleted';
  if (row.authDisabled && row.lifecycle === 'existing') return 'auth-disabled mismatch';
  if (row.invitedNotSignedIn) return 'invited, not yet signed in';
  if (row.fallbackLegacy) return 'still on legacy store';
  if (row.lastSessionError) return row.lastSessionError;
  return null;
}
